import { motion } from "framer-motion";
import { Coins, Clock, ImageOff } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 17 — Why short-form video matters.
 *
 *   1. Video is expensive.
 *   2. Editing eats the day.
 *   3. So agents post less — without the assets they actually need.
 *   4. Result: no traction.
 *
 * The "all from the same content" punchline lives in the speaker notes
 * (was a right-side panel · stripped out for breath).
 */

const GREEN = "#96C11F";
const AQUA = "#00deb6";
const RED = "#c83a4f";

const obstacles = [
  {
    icon: Coins,
    label: "Video is expensive.",
  },
  {
    icon: Clock,
    label: "Editing eats the day.",
  },
  {
    icon: ImageOff,
    label: "Agents post less without the assets they actually need.",
  },
];

export default function Scene17_Previously() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-shortform-why">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.14) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto w-full">
        {/* Hero header */}
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-6xl lg:text-7xl leading-[1.0] mb-14 max-w-[1200px]"
          data-testid="shortform-hero-header"
        >
          <span style={{ color: "#e2e8f0" }}>Why </span>
          <span
            style={{
              background: `linear-gradient(135deg, ${GREEN} 0%, ${AQUA} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontWeight: 900,
            }}
          >
            short-form
          </span>{" "}
          <span style={{ color: "#e2e8f0" }}>video matters.</span>
        </motion.h2>

        {/* Three stacked obstacle rows · all in green now */}
        <ul className="space-y-5 max-w-4xl">
          {obstacles.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.li
                key={o.label}
                initial={{ opacity: 0, x: -20, filter: "blur(6px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  delay: 0.7 + i * 0.32,
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="flex items-start gap-5"
                data-testid={`shortform-obstacle-${i}`}
              >
                <span
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    background: `${GREEN}1a`,
                    color: GREEN,
                    border: `1px solid ${GREEN}55`,
                  }}
                >
                  <Icon className="w-5 h-5" />
                </span>
                <span
                  className="kn-display text-2xl sm:text-3xl"
                  style={{ color: "#e2e8f0", lineHeight: 1.18, fontWeight: 700 }}
                >
                  {o.label}
                </span>
              </motion.li>
            );
          })}
        </ul>

        {/* Result chip — kept, the punchline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.9 }}
          className="mt-12 inline-flex items-center gap-3 rounded-full px-5 py-2.5"
          style={{
            background: "rgba(200,58,79,0.12)",
            border: `1px solid ${RED}55`,
          }}
          data-testid="shortform-result"
        >
          <span
            className="text-[11px] tracking-[0.32em] uppercase"
            style={{ color: "rgba(255,150,160,0.85)", fontWeight: 700 }}
          >
            Result
          </span>
          <span
            className="w-px h-3"
            style={{ background: "rgba(255,255,255,0.20)" }}
          />
          <span
            className="text-base sm:text-lg"
            style={{ color: "#fff", fontWeight: 700 }}
          >
            No traction.
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}
